'use client';

import { memo, useState, useEffect, useRef } from 'react';
import { useT } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';
import { ExternalLink } from 'lucide-react';
import type { PlatformMatch } from '@/lib/platform-registry';
// Extracted so it can be EXECUTED in a test rather than pattern-matched.
// See lib/widgets/frame-capability.ts for why that distinction mattered.
import { canFrameThirdParty } from '@/lib/widgets/frame-capability';

function aspectToPadding(ratio: string): string {
  if (ratio === 'auto') return '56.25%';
  const [w, h] = ratio.split(':').map(Number);
  if (!w || !h) return '56.25%';
  return `${(h / w) * 100}%`;
}

/** `<iframe credentialless>`, not in React's typings; see the usage below. */
const CREDENTIALLESS_ATTR: Record<string, string> = { credentialless: '' };


export const PlatformEmbed = memo(function PlatformEmbed({
  match,
  compact,
  className,
}: {
  match: PlatformMatch;
  compact?: boolean;
  className?: string;
}) {
  if (match.platform === 'X') {
    return <XEmbed match={match} compact={compact} className={className} />;
  }

  return (
    <IframeEmbed match={match} compact={compact} className={className} />
  );
});

function IframeEmbed({
  match,
  compact,
  className,
}: {
  match: PlatformMatch;
  compact?: boolean;
  className?: string;
}) {
  const [mode, setMode] = useState<'embed' | 'fallback'>('embed');
  const padding = aspectToPadding(match.aspectRatio);

  // The real gate. Runs once on mount, after hydration has matched the server's
  // optimistic `embed`, and drops straight to the poster card on an engine that
  // cannot show the frame at all. See `canFrameThirdParty` for why this cannot
  // be an `error` handler.
  useEffect(() => {
    if (!canFrameThirdParty()) setMode('fallback');
  }, []);

  return (
    <div className={cn('overflow-hidden', compact ? 'rounded-none -mx-3 sm:-mx-4' : 'rounded-xl', className)}>
      <div className="relative w-full" style={{ paddingBottom: padding }}>
        {mode === 'embed' ? (
          <iframe
            src={match.embedUrl}
            // The workspace is cross-origin isolated (ADR-0044) and a platform
            // embed never sends COEP, so inside it this frame is refused unless
            // it opts into a credential-less partition. Chromium only, and the
            // embed renders signed out there because the partition is fresh.
            // Where the attribute is unimplemented it is simply ignored and the
            // frame is refused, which is what the effect above pre-empts.
            {...CREDENTIALLESS_ATTR}
            title={`${match.platform} embed`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
            loading="lazy"
            // Kept for ordinary network failures only. It does NOT fire for a
            // COEP refusal, which is the case that matters here.
            onError={() => setMode('fallback')}
          />
        ) : (
          <EmbedFallback match={match} />
        )}
      </div>
    </div>
  );
}

function EmbedFallback({ match }: { match: PlatformMatch }) {
  return (
    <a
      href={match.originalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="absolute inset-0 group flex flex-col items-center justify-center bg-muted"
    >
      {match.thumbnailUrl ? (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={match.thumbnailUrl} alt={match.platform} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
            <div
              className="h-14 w-14 rounded-full flex items-center justify-center shadow-lg"
              style={{ background: match.color }}
            >
              <svg viewBox="0 0 24 24" className="h-7 w-7 text-white fill-white ml-0.5">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <ExternalLink className="h-8 w-8" />
          <span className="text-sm font-medium">Open on {match.platform}</span>
        </div>
      )}
      <span className="absolute bottom-2 right-2 text-[10px] text-white/70 bg-black/50 px-1.5 py-0.5 rounded">
        Open on {match.platform}
      </span>
    </a>
  );
}

function XEmbed({
  match,
  compact,
  className,
}: {
  match: PlatformMatch;
  compact?: boolean;
  className?: string;
}) {
  const t = useT();
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const win = window as unknown as { twttr?: { widgets?: { createTweet: (id: string, el: HTMLElement, opts: Record<string, unknown>) => Promise<unknown> } } };

    const render = () => {
      if (!containerRef.current) return;
      win.twttr?.widgets?.createTweet(match.id, containerRef.current, {
        theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
        conversation: 'none',
        dnt: true,
      }).then(() => setLoaded(true));
    };

    if (win.twttr?.widgets) {
      render();
    } else {
      const script = document.createElement('script');
      script.src = 'https://platform.twitter.com/widgets.js';
      // CORS mode, deliberately. Under `Cross-Origin-Embedder-Policy:
      // require-corp` a plain script tag is a NO-CORS load, which needs a
      // `Cross-Origin-Resource-Policy` header the host does not send. It does
      // send `Access-Control-Allow-Origin: *` (checked 2026-09-22), and that
      // only counts once the request is made in CORS mode. One attribute is
      // the difference between this script loading on an isolated page and
      // being blocked outright.
      script.crossOrigin = 'anonymous';
      script.async = true;
      script.onload = render;
      document.head.appendChild(script);
    }
  }, [match.id]);

  return (
    <div className={cn('overflow-hidden', compact ? '-mx-3 sm:-mx-4' : '', className)}>
      <div ref={containerRef} className="flex justify-center" />
      {!loaded && (
        <a
          href={match.originalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm text-primary hover:underline p-3 rounded-lg bg-muted/50"
        >
          <ExternalLink className="h-4 w-4 shrink-0" />
          <span>{t('platformEmbed.loadingX')}</span>
        </a>
      )}
    </div>
  );
}

export interface LinkMeta {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  favicon?: string;
}

export function RichLinkCard({
  url,
  meta,
  className,
}: {
  url: string;
  meta: LinkMeta;
  className?: string;
}) {
  let hostname = '';
  try { hostname = new URL(url).hostname; } catch { /* */ }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'group flex overflow-hidden rounded-xl border border-border hover:border-primary/30 transition-colors bg-card',
        meta.image ? 'flex-col sm:flex-row' : '',
        className,
      )}
    >
      {meta.image && (
        <div className="sm:w-48 sm:min-h-[120px] shrink-0 overflow-hidden bg-muted">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={meta.image}
            alt={meta.title || ''}
            className="w-full h-32 sm:h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      <div className="flex flex-col justify-center gap-1 p-3 min-w-0">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {meta.favicon && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={meta.favicon} alt="" className="h-3.5 w-3.5 rounded-sm" />
          )}
          <span className="truncate">{meta.siteName || hostname}</span>
        </div>
        {meta.title && (
          <p className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">
            {meta.title}
          </p>
        )}
        {meta.description && (
          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
            {meta.description}
          </p>
        )}
      </div>
    </a>
  );
}
